import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, useMap, useMapEvents, Polygon } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { Loader2, MapPin, Search, RefreshCw, Filter, Activity, Clock, ExternalLink, Building2, X, Pentagon, Trash2, Plus, Check, ListChecks, ChevronLeft, ChevronRight } from 'lucide-react';
import { telemetryApi, depotsApi, geofencesApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';
import { useAuth } from '../context/AuthContext';

// Free OSM tiles — no API key required. Same source as the per-asset drawer.
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Default Leaflet icons ship as broken paths under CRA. Reload from CDN once.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Health classifier — single source of truth for marker colour + filters.
const STALE_MIN = 30 * 60 * 1000;          // 30m → "stale"
const OFFLINE_MIN = 24 * 60 * 60 * 1000;   // 24h → "offline"

const healthOf = (asset) => {
  const updated = asset?.last_position?.updated_at;
  if (!updated) return 'no_position';
  const age = Date.now() - new Date(updated).getTime();
  if (age >= OFFLINE_MIN) return 'offline';
  if (age >= STALE_MIN) return 'stale';
  return 'online';
};

const HEALTH_META = {
  online: { label: 'Online', dot: '#10b981', ring: 'rgba(16,185,129,0.25)' },
  stale: { label: 'Stale', dot: '#f59e0b', ring: 'rgba(245,158,11,0.25)' },
  offline: { label: 'Offline', dot: '#ef4444', ring: 'rgba(239,68,68,0.25)' },
  no_position: { label: 'No position', dot: '#6b7280', ring: 'rgba(107,114,128,0.25)' },
};

const formatAgo = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
};

// Build a Leaflet divIcon coloured by asset health. Inline SVG keeps it
// crisp at any zoom without requiring extra asset files.
const buildIcon = (health, ignition) => {
  const m = HEALTH_META[health] || HEALTH_META.no_position;
  const pulse = health === 'online' && ignition
    ? `<span style="position:absolute;inset:-6px;border-radius:50%;background:${m.ring};animation:fmPulse 1.8s ease-out infinite;"></span>`
    : '';
  return L.divIcon({
    html: `
      <div style="position:relative;width:18px;height:18px;">
        ${pulse}
        <span style="position:absolute;inset:0;border-radius:50%;background:${m.dot};border:2px solid #000;box-shadow:0 0 0 3px ${m.ring};"></span>
      </div>`,
    className: 'fleet-map-pin',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
};

// Reach into the Leaflet instance imperatively to swap the cluster layer
// whenever the filtered list changes. Building the cluster outside React
// keeps it performant at 400+ markers.
const ClusterLayer = ({ assets, onAssetClick }) => {
  const map = useMap();
  const groupRef = useRef(null);
  const boundsAppliedRef = useRef(false);

  useEffect(() => {
    if (!map) return;
    if (!groupRef.current) {
      groupRef.current = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
        maxClusterRadius: 60,
        // Custom yellow cluster bubble — matches the EQUIPR primary accent.
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          const size = count >= 100 ? 56 : count >= 25 ? 48 : 40;
          return L.divIcon({
            html: `
              <div style="
                width:${size}px;height:${size}px;border-radius:50%;
                background:rgba(255,209,8,0.92);color:#0a0a0a;
                border:3px solid #000;display:flex;align-items:center;
                justify-content:center;font-weight:800;font-size:13px;
                box-shadow:0 0 0 4px rgba(255,209,8,0.18);
              ">${count}</div>`,
            className: 'fleet-map-cluster',
            iconSize: [size, size],
          });
        },
      });
      map.addLayer(groupRef.current);
    }

    const group = groupRef.current;
    group.clearLayers();

    const markers = [];
    assets.forEach((a) => {
      const lat = Number(a?.last_position?.latitude);
      const lng = Number(a?.last_position?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const m = L.marker([lat, lng], {
        icon: buildIcon(healthOf(a), a?.last_position?.ignition),
      });
      m.on('click', () => onAssetClick(a));
      markers.push(m);
    });

    if (markers.length) {
      group.addLayers(markers);
    }

    // Auto-fit on first non-empty load only — afterwards the user is driving
    // the viewport themselves and we mustn't yank it around on refresh.
    if (!boundsAppliedRef.current && markers.length) {
      const bounds = group.getBounds();
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13 });
        boundsAppliedRef.current = true;
      }
    }

    return () => {
      // Don't remove the layer between renders — re-using it preserves zoom
      // state. The component cleanup at unmount handles disposal.
    };
  }, [map, assets, onAssetClick]);

  useEffect(() => () => {
    if (groupRef.current && map) {
      map.removeLayer(groupRef.current);
      groupRef.current = null;
    }
  }, [map]);

  return null;
};

const StatBadge = ({ tone, label, count, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs uppercase tracking-wider transition-all ${
      active
        ? 'bg-secondary border-primary text-foreground'
        : 'bg-card/80 border-border text-muted-foreground hover:text-foreground'
    }`}
    data-testid={testId}
  >
    <span className="w-2 h-2 rounded-full" style={{ background: tone }} />
    <span className="font-semibold">{label}</span>
    <span className="font-mono">{count}</span>
  </button>
);

// Renders all saved geofences + the live polygon being drawn. Kept as an
// inline child of MapContainer so it inherits the map context cleanly.
const GeofenceLayer = ({ geofences, drawPoints, drawMode }) => (
  <>
    {geofences.map((f) => (
      <Polygon
        key={f.id}
        positions={f.polygon}
        pathOptions={{
          color: f.color || '#FFD108',
          weight: 2,
          fillOpacity: 0.12,
          dashArray: f.active === false ? '6, 6' : undefined,
        }}
      />
    ))}
    {drawMode && drawPoints.length >= 2 && (
      <Polygon
        positions={drawPoints}
        pathOptions={{ color: '#FFD108', weight: 2, dashArray: '4, 6', fillOpacity: 0.08 }}
      />
    )}
  </>
);

// Captures left-clicks while draw mode is active and appends a vertex.
// Right-clicks remove the last vertex so corrections don't need a restart.
const DrawCapture = ({ enabled, onAdd, onUndo }) => {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onAdd([e.latlng.lat, e.latlng.lng]);
    },
    contextmenu(e) {
      if (!enabled) return;
      e.originalEvent?.preventDefault?.();
      onUndo();
    },
  });
  return null;
};

const FleetMapPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [assets, setAssets] = useState([]);
  const [depots, setDepots] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [depotFilter, setDepotFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // online/stale/offline/no_position
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // ── Geofence state ─────────────────────────────────────────────────
  const [showGeofences, setShowGeofences] = useState(true);
  const [drawMode, setDrawMode] = useState(false);
  const [drawPoints, setDrawPoints] = useState([]); // [[lat, lng], …]
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState(null); // captured polygon at finish time
  const [eventsOpen, setEventsOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const [posRes, depotRes, fenceRes] = await Promise.all([
        telemetryApi.getLastPositions(),
        depotsApi.list().catch(() => ({ data: [] })),
        geofencesApi.list().catch(() => ({ data: [] })),
      ]);
      setAssets(posRes.data || []);
      setDepots(depotRes.data || []);
      setGeofences(fenceRes.data || []);
      setLastRefreshed(new Date());
    } catch (e) {
      toast.error('Failed to load fleet positions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await geofencesApi.events({ limit: 50 });
      setEvents(res.data || []);
    } catch {
      toast.error('Failed to load geofence events');
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (eventsOpen) loadEvents();
  }, [eventsOpen, loadEvents]);

  useEffect(() => {
    fetchData();
    // Silent refresh every 30s — keeps the map alive without strobing the UI.
    const id = setInterval(() => fetchData(true), 30_000);
    return () => clearInterval(id);
  }, []);

  // Counts are computed against the raw fleet so the badges always show the
  // global picture, even while a filter is narrowing the visible markers.
  const counts = useMemo(() => {
    const c = { total: assets.length, online: 0, stale: 0, offline: 0, no_position: 0 };
    assets.forEach((a) => { c[healthOf(a)] += 1; });
    return c;
  }, [assets]);

  const visibleAssets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (statusFilter !== 'all' && healthOf(a) !== statusFilter) return false;
      if (depotFilter !== 'all' && a.depot_id !== depotFilter) return false;
      if (q) {
        const haystack = [
          a.name, a.unit_number, a.serial_number,
          a.make, a.model, a.telemetry_imei,
        ].filter(Boolean).map((s) => String(s).toLowerCase()).join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [assets, search, depotFilter, statusFilter]);

  const onAssetClick = (a) => setSelectedAsset(a);

  return (
    <div className="space-y-4" data-testid="fleet-map-page">
      {/* Inject keyframes once — scoped via the className on the icon. */}
      <style>{`
        @keyframes fmPulse {
          0% { transform: scale(0.85); opacity: 0.9; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        .leaflet-container { background: #0a0a0a; }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight flex items-center gap-2">
            <MapPin className="w-6 h-6 text-primary" />
            Fleet Map
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live positions for every telematics-bound asset. Refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {lastRefreshed && (
            <span className="text-xs text-muted-foreground hidden sm:inline" data-testid="fleet-map-last-refreshed">
              Last refresh · {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEventsOpen(true)}
            data-testid="fleet-map-events-btn"
          >
            <ListChecks className="w-3.5 h-3.5 mr-2" />
            Geofence events
          </Button>
          <Button
            variant={showGeofences ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowGeofences((s) => !s)}
            data-testid="fleet-map-toggle-fences"
          >
            <Pentagon className="w-3.5 h-3.5 mr-2" />
            {showGeofences ? 'Hide' : 'Show'} fences ({geofences.length})
          </Button>
          {isAdmin && !drawMode && (
            <Button
              variant="default"
              size="sm"
              onClick={() => { setDrawMode(true); setDrawPoints([]); setSelectedAsset(null); }}
              data-testid="fleet-map-draw-start"
            >
              <Plus className="w-3.5 h-3.5 mr-2" />
              Draw geofence
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchData(true)}
            disabled={refreshing}
            data-testid="fleet-map-refresh"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Draw mode banner */}
      {drawMode && (
        <div className="bg-primary/10 border border-primary/40 rounded-sm px-3 py-2 flex flex-wrap items-center gap-3 text-sm" data-testid="fleet-map-draw-banner">
          <Pentagon className="w-4 h-4 text-primary" />
          <span className="font-semibold uppercase tracking-wide">Drawing mode</span>
          <span className="text-muted-foreground hidden sm:inline">
            Left-click the map to add vertices · right-click to undo · need at least 3 points.
          </span>
          <span className="text-xs font-mono ml-auto" data-testid="fleet-map-draw-count">
            {drawPoints.length} vertices
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDrawPoints((p) => p.slice(0, -1))}
            disabled={drawPoints.length === 0}
            data-testid="fleet-map-draw-undo"
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" />
            Undo
          </Button>
          <Button
            size="sm"
            variant="default"
            onClick={() => {
              if (drawPoints.length < 3) {
                toast.error('Add at least 3 vertices');
                return;
              }
              setPendingSave(drawPoints);
              setSaveDialogOpen(true);
            }}
            data-testid="fleet-map-draw-finish"
          >
            <Check className="w-3.5 h-3.5 mr-1" />
            Finish
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setDrawMode(false); setDrawPoints([]); }}
            data-testid="fleet-map-draw-cancel"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Cancel
          </Button>
        </div>
      )}

      {/* Filters + Status Badges */}
      <div className="bg-card border border-border rounded-sm p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatBadge tone="#FFD108" label="Total" count={counts.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} testId="fleet-map-filter-all" />
          <StatBadge tone={HEALTH_META.online.dot} label="Online" count={counts.online} active={statusFilter === 'online'} onClick={() => setStatusFilter('online')} testId="fleet-map-filter-online" />
          <StatBadge tone={HEALTH_META.stale.dot} label="Stale" count={counts.stale} active={statusFilter === 'stale'} onClick={() => setStatusFilter('stale')} testId="fleet-map-filter-stale" />
          <StatBadge tone={HEALTH_META.offline.dot} label="Offline" count={counts.offline} active={statusFilter === 'offline'} onClick={() => setStatusFilter('offline')} testId="fleet-map-filter-offline" />
          {counts.no_position > 0 && (
            <StatBadge tone={HEALTH_META.no_position.dot} label="No fix" count={counts.no_position} active={statusFilter === 'no_position'} onClick={() => setStatusFilter('no_position')} testId="fleet-map-filter-no-position" />
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search name, unit #, serial or IMEI…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="fleet-map-search"
            />
          </div>
          <Select value={depotFilter} onValueChange={setDepotFilter}>
            <SelectTrigger className="w-full md:w-56" data-testid="fleet-map-depot-filter">
              <Building2 className="w-3.5 h-3.5 mr-2" />
              <SelectValue placeholder="All depots" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All depots</SelectItem>
              {depots.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || depotFilter !== 'all' || statusFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearch(''); setDepotFilter('all'); setStatusFilter('all'); }}
              data-testid="fleet-map-clear-filters"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear filters
            </Button>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
          <Filter className="w-3 h-3" />
          Showing <span className="font-mono text-foreground">{visibleAssets.length}</span> of {counts.total} telematics-enabled assets.
        </p>
      </div>

      {/* Map + Side panel */}
      <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 h-[calc(100vh-360px)] min-h-[520px]">
        <div className="relative border border-border rounded-sm overflow-hidden bg-[#0a0a0a]" data-testid="fleet-map-canvas">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <MapContainer
              center={[-25.2744, 133.7751]} // Sensible Australia-wide default; auto-fit overrides on first load
              zoom={4}
              scrollWheelZoom
              style={{ height: '100%', width: '100%', cursor: drawMode ? 'crosshair' : '' }}
            >
              <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
              {showGeofences && (
                <GeofenceLayer geofences={geofences} drawPoints={drawPoints} drawMode={drawMode} />
              )}
              <DrawCapture
                enabled={drawMode}
                onAdd={(pt) => setDrawPoints((p) => [...p, pt])}
                onUndo={() => setDrawPoints((p) => p.slice(0, -1))}
              />
              <ClusterLayer assets={visibleAssets} onAssetClick={onAssetClick} />
            </MapContainer>
          )}
          {!loading && visibleAssets.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-card/95 border border-border rounded-sm p-6 text-center max-w-xs pointer-events-auto">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm font-semibold">No matching assets</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {counts.total === 0
                    ? 'No telematics-enabled assets yet. Enable Telemetry on an asset to see it here.'
                    : 'Try clearing the filters or widening the search.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Side panel — selected asset detail OR geofences list */}
        <div className="hidden lg:flex flex-col gap-3 min-h-0">
          {selectedAsset && (
            <SideDetailPanel asset={selectedAsset} onClose={() => setSelectedAsset(null)} />
          )}
          <GeofenceListPanel
            geofences={geofences}
            isAdmin={isAdmin}
            collapsed={!!selectedAsset}
            onDelete={(id) => {
              confirm({
                title: 'Delete this geofence?',
                description: 'Future enter/exit events will stop firing for this fence.',
                testId: 'confirm-delete-geofence',
                onConfirm: async () => {
                  try {
                    await geofencesApi.delete(id);
                    setGeofences((arr) => arr.filter((g) => g.id !== id));
                    toast.success('Geofence deleted');
                  } catch {
                    toast.error('Failed to delete geofence');
                  }
                },
              });
            }}
          />
        </div>
      </div>

      {/* Save geofence dialog */}
      <GeofenceSaveDialog
        open={saveDialogOpen}
        polygon={pendingSave}
        depots={depots}
        onClose={() => { setSaveDialogOpen(false); }}
        onSaved={(created) => {
          setGeofences((arr) => [created, ...arr]);
          setSaveDialogOpen(false);
          setDrawMode(false);
          setDrawPoints([]);
          setPendingSave(null);
          toast.success(`Geofence «${created.name}» saved`);
        }}
      />

      {/* Geofence events dialog */}
      <Dialog open={eventsOpen} onOpenChange={setEventsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-primary" />
              Recent geofence events
            </DialogTitle>
            <DialogDescription>
              Last 50 enter/exit transitions across your fleet.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1">
            {eventsLoading ? (
              <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            ) : events.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No events yet — they appear as soon as a telematics-enabled asset crosses a fence boundary.</p>
            ) : (
              <ul className="space-y-2" data-testid="fleet-map-events-list">
                {events.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 border border-border rounded-sm px-3 py-2 text-sm">
                    <span
                      className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest ${
                        e.event_type === 'enter'
                          ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                          : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                      }`}
                    >
                      {e.event_type}
                    </span>
                    <span className="font-medium truncate flex-1">{e.asset_label}</span>
                    <span className="text-muted-foreground truncate">«{e.geofence_name || 'fence'}»</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatAgo(e.occurred_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={loadEvents} disabled={eventsLoading}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

// ── Geofence side panel ─────────────────────────────────────────────
const GeofenceListPanel = ({ geofences, isAdmin, onDelete, collapsed }) => {
  if (geofences.length === 0) {
    return (
      <div className={`border border-border rounded-sm bg-card p-4 text-center text-sm text-muted-foreground ${collapsed ? 'hidden xl:block' : ''}`}>
        <Pentagon className="w-7 h-7 mx-auto mb-2 opacity-50" />
        <p className="font-medium text-foreground">No geofences yet</p>
        <p className="text-xs mt-1">
          {isAdmin
            ? 'Click "Draw geofence" above to outline a job site or depot. Assets crossing the boundary will trigger in-app alerts.'
            : 'Ask an admin to draw a fence to start tracking enter/exit events on this map.'}
        </p>
      </div>
    );
  }
  return (
    <div className={`border border-border rounded-sm bg-card flex flex-col min-h-0 ${collapsed ? 'hidden xl:flex' : ''}`} data-testid="fleet-map-geofence-list">
      <div className="flex items-center justify-between p-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Pentagon className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">Geofences</h3>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground">{geofences.length}</span>
      </div>
      <ul className="overflow-y-auto p-2 space-y-2 max-h-[280px]">
        {geofences.map((f) => (
          <li key={f.id} className="rounded-sm border border-[#E6E2D9] bg-white p-2.5 text-xs space-y-1 hover:border-[#D4CFC2] transition-colors" data-testid={`fleet-map-geofence-${f.id}`}>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: f.color || '#FFD108' }} />
              <span className="font-semibold truncate flex-1" title={f.name}>{f.name}</span>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => onDelete(f.id)}
                  className="text-muted-foreground hover:text-red-400 transition-colors"
                  data-testid={`fleet-map-geofence-delete-${f.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {f.description && <p className="text-muted-foreground line-clamp-2">{f.description}</p>}
            <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
              <Badge variant="outline" className="text-[9px] uppercase text-[#57534E] border-[#D4CFC2]">
                {f.assets_inside ?? 0} inside
              </Badge>
              {f.alert_on_enter && <Badge variant="outline" className="text-[9px] uppercase text-emerald-700 border-emerald-600/40">Enter</Badge>}
              {f.alert_on_exit && <Badge variant="outline" className="text-[9px] uppercase text-amber-700 border-amber-600/40">Exit</Badge>}
              {f.active === false && <Badge variant="outline" className="text-[9px] uppercase text-muted-foreground">Inactive</Badge>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── Save dialog ─────────────────────────────────────────────────────
const GeofenceSaveDialog = ({ open, polygon, depots, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#FFD108');
  const [alertEnter, setAlertEnter] = useState(true);
  const [alertExit, setAlertExit] = useState(true);
  const [depotId, setDepotId] = useState('none');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setColor('#FFD108');
      setAlertEnter(true);
      setAlertExit(true);
      setDepotId('none');
    }
  }, [open]);

  const handleSave = async () => {
    if (!name.trim() || !polygon || polygon.length < 3) return;
    setSaving(true);
    try {
      const res = await geofencesApi.create({
        name: name.trim(),
        description: description.trim() || undefined,
        polygon,
        color,
        alert_on_enter: alertEnter,
        alert_on_exit: alertExit,
        depot_id: depotId !== 'none' ? depotId : undefined,
        active: true,
      });
      onSaved({ ...res.data, assets_inside: 0 });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save geofence';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save geofence');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="fleet-map-save-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pentagon className="w-4 h-4 text-primary" />
            Save geofence
          </DialogTitle>
          <DialogDescription>
            {polygon ? `${polygon.length} vertices captured.` : ''} Set a name and choose which events should notify your team.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="gf-name" className="text-xs uppercase tracking-wider">Name</Label>
            <Input id="gf-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. North Site yard" data-testid="fleet-map-save-name" />
          </div>
          <div>
            <Label htmlFor="gf-desc" className="text-xs uppercase tracking-wider">Description (optional)</Label>
            <Input id="gf-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes for your team" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="gf-color" className="text-xs uppercase tracking-wider">Colour</Label>
              <Input id="gf-color" type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 p-1 cursor-pointer" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Depot (optional)</Label>
              <Select value={depotId} onValueChange={setDepotId}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-sm border border-border bg-secondary/20 px-3 py-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="gf-enter" className="text-xs">Notify on <span className="text-emerald-400 font-semibold">enter</span></Label>
              <Switch id="gf-enter" checked={alertEnter} onCheckedChange={setAlertEnter} data-testid="fleet-map-save-alert-enter" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="gf-exit" className="text-xs">Notify on <span className="text-amber-400 font-semibold">exit</span></Label>
              <Switch id="gf-exit" checked={alertExit} onCheckedChange={setAlertExit} data-testid="fleet-map-save-alert-exit" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="fleet-map-save-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !name.trim()} data-testid="fleet-map-save-confirm">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-2" />}
            Save geofence
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SideDetailPanel = ({ asset, onClose }) => {
  if (!asset) {
    return (
      <div className="hidden lg:flex flex-col border border-border rounded-sm bg-card p-4 text-center items-center justify-center text-sm text-muted-foreground">
        <MapPin className="w-7 h-7 mb-2 opacity-50" />
        <p className="font-medium text-foreground">Select an asset</p>
        <p className="text-xs mt-1">Click any marker to see its live status, position and quick links.</p>
      </div>
    );
  }
  const health = healthOf(asset);
  const meta = HEALTH_META[health];
  const pos = asset.last_position || {};
  const label = asset.unit_number || asset.name || asset.serial_number || 'Asset';

  return (
    <div className="border border-border rounded-sm bg-card overflow-hidden flex flex-col" data-testid="fleet-map-side-panel">
      <div className="flex items-start justify-between gap-2 p-4 border-b border-border">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Selected</p>
          <h3 className="font-semibold text-base truncate" title={label} data-testid="fleet-map-side-panel-label">{label}</h3>
          {(asset.make || asset.model) && (
            <p className="text-xs text-muted-foreground truncate">{[asset.make, asset.model].filter(Boolean).join(' ')}</p>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="-mt-1 -mr-1" data-testid="fleet-map-side-panel-close">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-4 space-y-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: meta.dot }} />
          <span className="font-semibold uppercase tracking-wider">{meta.label}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{formatAgo(pos.updated_at)}</span>
        </div>

        {asset.depot_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="w-3.5 h-3.5" />
            <span>{asset.depot_name}</span>
          </div>
        )}

        {asset.telemetry_imei && (
          <div className="font-mono text-[11px] text-muted-foreground">
            IMEI · <span className="text-foreground">{asset.telemetry_imei}</span>
          </div>
        )}

        {(asset.telemetry_source === 'vdaq' || pos.source === 'vdaq') && (
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EAF2FF] border border-[#C3DAFB] text-[10px] font-semibold text-[#1F4E8C]" data-testid="fleet-map-vdaq-tag">
            via V-DAQ
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Cell label="Lat" value={pos.latitude != null ? Number(pos.latitude).toFixed(5) : '—'} mono />
          <Cell label="Lng" value={pos.longitude != null ? Number(pos.longitude).toFixed(5) : '—'} mono />
          <Cell label="Speed" value={pos.speed_kmh != null ? `${Number(pos.speed_kmh).toFixed(1)} km/h` : '—'} />
          <Cell label="Ignition" value={pos.ignition == null ? '—' : (pos.ignition ? 'On' : 'Off')} tone={pos.ignition ? 'ok' : undefined} />
          {asset.hours != null && (
            <Cell label={`Meter (${(asset.hours_unit || 'h').toString()})`} value={Number(asset.hours).toLocaleString(undefined, { maximumFractionDigits: 1 })} />
          )}
          {asset.secondary_meter != null && (
            <Cell label={`2nd (${(asset.secondary_meter_unit || '').toString()})`} value={Number(asset.secondary_meter).toLocaleString(undefined, { maximumFractionDigits: 1 })} />
          )}
        </div>

        {asset.status && (
          <Badge variant="outline" className="uppercase text-[10px]">{String(asset.status).replace(/_/g, ' ')}</Badge>
        )}
      </div>

      <div className="mt-auto p-3 border-t border-border space-y-2">
        <Link to={`/assets/${asset.id}`} className="block">
          <Button variant="default" size="sm" className="w-full" data-testid="fleet-map-side-panel-open-asset">
            Open asset
          </Button>
        </Link>
        {Number.isFinite(Number(pos.latitude)) && Number.isFinite(Number(pos.longitude)) && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => window.open(`https://www.google.com/maps?q=${pos.latitude},${pos.longitude}`, '_blank', 'noopener,noreferrer')}
            data-testid="fleet-map-side-panel-google"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-2" />
            Google Maps
          </Button>
        )}
      </div>
    </div>
  );
};

const Cell = ({ label, value, mono, tone }) => (
  <div className="rounded-sm border border-border bg-secondary/30 px-2 py-1.5">
    <div className="text-[9px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
      <Activity className="w-2.5 h-2.5" />{label}
    </div>
    <div className={`text-xs font-semibold ${mono ? 'font-mono' : ''} ${tone === 'ok' ? 'text-emerald-400' : ''}`}>{value}</div>
  </div>
);

export default FleetMapPage;
